import Moment from 'moment-timezone';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { Pagination } from '@material-ui/lab';

import { Chip, Link } from '@material-ui/core';

import classNames from 'classnames';

import LogsEmptyState from '../webhook-logs/webhook-logs-empty-state';

import useStyles from './style';

import RequestLogsFilters from './request-logs-filters';

import RequestLogDetail from './request-log-details';

import useDefaultStyles from 'assets/styles/style';

import { Header, ScrollView, View } from 'components';
import PageDescription from 'components/page-description/page-description';
import { PaginationProps, PAGINATION_LIMIT, WebhookDateRange } from 'types';
import { openNewTab } from 'helpers/open-new-tab';
import BasicTable from 'components/table';

import { getDateRange } from 'helpers/format.helper';
import {
  getRequestLogs,
  isRequestLogsLoading,
} from 'store/features/developer/request-logs/request-logs.selector';
import { fetchRequestLogsApi } from 'store/features/developer/request-logs/request-logs.action';
import { RequestLogsStatus } from 'types/interface/request-logs.interface';

interface Props {
  testid?: string;
}

const REQUEST_LOGS_DOC_LINK = 'https://docs.railz.ai/reference/request-logs';

const RequestLogsPage: React.FC<Props> = ({ testid = 'test-header-request-logs' }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const classes = useStyles();
  const defaultClasses = useDefaultStyles();

  const [queryFilter, setQueryFilter] = useState(getDateRange(0, 7, 0));
  const [selectedLog, setSelectedLog] = useState(null);
  const [currentPage, setCurrentPage] = useState<number>(1);

  const isLoading = useSelector(isRequestLogsLoading);
  const requestLogs = useSelector(getRequestLogs);

  useEffect(() => {
    refreshRequestLogs(queryFilter);
  }, []);

  const refreshRequestLogs = (params?: PaginationProps & WebhookDateRange): any => {
    let localOffset = Number((currentPage - 1) * PAGINATION_LIMIT);
    if (params?.offset !== undefined) localOffset = params?.offset;

    const payload = {
      ...params,
      limit: params?.limit || PAGINATION_LIMIT,
      offset: localOffset,
    };
    return dispatch(fetchRequestLogsApi(payload));
  };

  const openRequestDetails = (row): void => {
    setSelectedLog(row);
  };

  const closeRequestDetails = (): void => {
    setSelectedLog(null);
  };

  const handleSelectedFilter = (filterParams): void => {
    setQueryFilter(filterParams);
    setSelectedLog(null);
    setCurrentPage(1);
  };

  const columns = [
    {
      key: 'statusCode',
      name: t('DASHBOARD_DEVELOPERS_WEBHOOKS_LOG_STATUS'),
      className: classNames(classes.tableHead, classes.statusColumn),
      render: ({ status, statusCode }): React.ReactElement => (
        <Chip
          className={`${classes.chip} ${
            status === RequestLogsStatus.success
              ? `${classes.tableChipActive} ${classes.chipActive}`
              : `${classes.tableChipInactive} ${classes.chipInactive}`
          }`}
          data-testid={`request-logs-status-chip`}
          label={statusCode}
          size="small"
        />
      ),
    },
    {
      key: 'method',
      name: t('DASHBOARD_DEVELOPERS_REQUEST_LOGS_METHOD'),
      className: classNames(classes.tableHead, classes.methodColumn),
      render: ({ method }): React.ReactElement => (
        <Chip
          className={`${classes.chip} ${classes.chipNeutral}`}
          data-testid={`request-method-chip`}
          label={method}
          size="small"
        />
      ),
    },
    {
      key: 'connectionId',
      name: t('DASHBOARD_DEVELOPERS_WEBHOOKS_LOG_CONNECTIONUUID'),
      className: classNames(classes.tableHead, classes.connUuidColumn),
      render: ({ connectionId }): React.ReactElement => (
        <span className={classes.rowText}>{connectionId || '-'}</span>
      ),
    },
    {
      key: 'url',
      name: t('DASHBOARD_DEVELOPERS_REQUEST_LOGS_API_ENDPOINT'),
      className: classNames(classes.tableHead, classes.urlColumn),
      render: ({ url }): React.ReactElement => <span className={classes.rowText}>{url}</span>,
    },
    {
      key: 'timestamp',
      name: t('DASHBOARD_DEVELOPERS_WEBHOOKS_LOG_TIME_STAMP'),
      className: classNames(classes.tableHead, classes.timestampColumn),
      render: ({ timestamp }): React.ReactElement => (
        <span className={`${classes.rowTextWrap} ${classes.rowText}`}>
          {Moment(timestamp).format('DD MMM YYYY hh:mm:ss A')}
        </span>
      ),
    },
  ];

  return (
    <>
      <Header
        drawerMenu={true}
        leftComponent={null}
        rightComponent={null}
        title={t('DASHBOARD_DEVELOPERS_REQUEST_LOGS_TITLE')}
        testId={testid}
      />
      <View className={classes.view}>
        <div className={`${classes.pageDesc} ${selectedLog && 'selected'}`}>
          <PageDescription
            title="DASHBOARD_DEVELOPERS_REQUEST_LOGS_HEADER"
            subtitle="DASHBOARD_DEVELOPERS_REQUEST_LOGS_DESCRIPTION"
            subtitleComponents={{
              a: (
                <Link
                  href={REQUEST_LOGS_DOC_LINK}
                  onClick={(e): void => openNewTab(e, REQUEST_LOGS_DOC_LINK)}
                  rel="noopener noreferrer"
                  className={defaultClasses.link}
                >
                  {t('DASHBOARD_LEARN_MORE_CTA')}
                </Link>
              ),
            }}
          />
        </div>
        <div className={classes.overflowDiv}>
          <RequestLogsFilters
            handleSelected={handleSelectedFilter}
            componentClasses={`${classes.filterContainer} ${selectedLog && 'selected'}`}
          />
          <div className={classes.mainGrid}>
            <div className={`${classes.leftSideContainer} ${selectedLog && 'selected'}`}>
              <ScrollView classNames={classes.scrollContainer}>
                {!isLoading && requestLogs?.data?.length === 0 ? (
                  <LogsEmptyState
                    hasWebhooks={true}
                    hasLogData={requestLogs?.data?.length > 0}
                    isRequestLogs={true}
                  />
                ) : (
                  <BasicTable
                    data-testid="request-logs-table"
                    columns={columns}
                    rows={requestLogs?.data}
                    stickyHeader={true}
                    isPaperComponent={false}
                    isHoverable={true}
                    onRowClick={{ actionFunction: openRequestDetails }}
                    isLoading={isLoading}
                    selectionKey={'requestId'}
                    selectionId={selectedLog?.requestId}
                  />
                )}
              </ScrollView>

              {requestLogs?.meta?.totalPages > 1 && (
                <div className={classes.pagination}>
                  <Pagination
                    count={requestLogs?.meta?.totalPages}
                    page={currentPage}
                    defaultPage={1}
                    onChange={(_, pagerNumber): void => {
                      setCurrentPage(pagerNumber);
                      refreshRequestLogs({
                        ...queryFilter,
                        offset: (pagerNumber - 1) * PAGINATION_LIMIT,
                      });
                    }}
                  />
                </div>
              )}
            </div>

            {selectedLog && (
              <div className={classes.rightSideContainer}>
                <RequestLogDetail requestLog={selectedLog} handleClose={closeRequestDetails} />
              </div>
            )}
          </div>
        </div>
      </View>
    </>
  );
};

export default RequestLogsPage;
